import {useCallback, useRef, useState} from "react";

/** Stable identity for a subject/topic group, usable as a React key and a cache key. */
export const groupKey = (group) => `s${group?.subjectId ?? "none"}:t${group?.topicId ?? "none"}`;

/**
 * Expand/collapse state for subject → topic groups whose contents are fetched the first time the
 * group is opened, then cached for the rest of the visit.
 *
 * Collapsing keeps what was already loaded, so re-opening a group is instant and doesn't re-hit the
 * API. A failed load is remembered per group so only that group shows an error (and retries on the
 * next open) — one bad group never blanks the whole screen.
 *
 * @param loadGroup (group) => Promise<any[]>
 */
export const useLazyGroups = (loadGroup) => {
    const [expanded, setExpanded] = useState({});
    const [items, setItems] = useState({});
    const [loading, setLoading] = useState({});
    const [errors, setErrors] = useState({});

    // Held in a ref so `toggle` keeps a stable identity even when the caller passes a new closure
    // on every render — otherwise every parent re-render would rebuild the group rows.
    const loadRef = useRef(loadGroup);
    loadRef.current = loadGroup;

    // Remembers the group object behind each key so `refresh` can re-run its loader.
    const groupsByKey = useRef({});

    const fetchInto = useCallback(async (key, group) => {
        setLoading((prev) => ({...prev, [key]: true}));
        setErrors((prev) => ({...prev, [key]: null}));
        try {
            const data = await loadRef.current(group);
            setItems((prev) => ({...prev, [key]: Array.isArray(data) ? data : []}));
        } catch (e) {
            setErrors((prev) => ({...prev, [key]: e}));
            // Leave the group open so the error is visible, and unset so the next open retries.
            setItems((prev) => {
                const next = {...prev};
                delete next[key];
                return next;
            });
        } finally {
            setLoading((prev) => ({...prev, [key]: false}));
        }
    }, []);

    const toggle = useCallback(async (group) => {
        const key = groupKey(group);
        groupsByKey.current[key] = group;
        const isOpen = expanded[key];
        setExpanded((prev) => ({...prev, [key]: !isOpen}));

        // Already open, already loaded, or mid-flight → nothing to fetch.
        if (isOpen || items[key] || loading[key]) return;
        await fetchInto(key, group);
    }, [expanded, items, loading, fetchInto]);

    /**
     * Re-fetch the groups that are currently open. Call after something changes the underlying data
     * (a teacher re-grading an open answer): collapsed groups just drop their stale cache and reload
     * on their next open, so nothing goes to the API that the reader isn't looking at.
     */
    const refresh = useCallback(async () => {
        const openKeys = Object.keys(expanded).filter((key) => expanded[key]);
        setItems((prev) => {
            const next = {};
            openKeys.forEach((key) => {
                if (prev[key]) next[key] = prev[key];
            });
            return next;
        });
        await Promise.all(openKeys.map((key) => fetchInto(key, groupsByKey.current[key])));
    }, [expanded, fetchInto]);

    return {expanded, items, loading, errors, toggle, refresh};
};

export default useLazyGroups;
